import type { ChatUiHotelOffer, HotelPlannerPlace } from '../hotel_planner/hotel_planner_schema.js';

/** Ponto geográfico em graus decimais. */
export type LatLng = { readonly lat: number; readonly lng: number };

const EARTH_RADIUS_KM = 6371;
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Distância em linha reta (haversine, Terra esférica) em km.
 *
 * A oferta já carrega `latitude`/`longitude` e o bloco carrega a âncora da busca
 * (`placeDetails`), então a distância é derivável no cliente — não precisa virar
 * campo do wire. Puro, sem IO.
 *
 * Existe uma cópia em `apps/services/trip` (metros, entidade de domínio de outro
 * serviço): um pacote compartilhado não pode importar de `apps/`, e o inverso
 * arrastaria React pro serviço. Duas linhas de trigonometria estável.
 */
export function distanceKm(a: LatLng, b: LatLng): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/** A distância até o ponto pesquisado, em km. A do FORNECEDOR manda: ela vem da
 *  mesma âncora que o haversine usaria e não depende de o bloco carregar
 *  `placeDetails` — sem ele, a conta local não tem de onde medir. O haversine
 *  fica como queda para bloco antigo, de antes de `distanceKm` existir.
 *
 *  M1 · Onda 2 (achado #2) — a régua ÚNICA: o card (`hotel_planner_view.ts`),
 *  o filtro do painel (`hotel_results_filter_view.ts`) e as diretivas puras do
 *  agente (`hotel_planner_directives.ts`) chamam esta MESMA função — nenhum
 *  lê `offer.distanceKm` cru por fora dela. Mora aqui (zero-React, ao lado do
 *  haversine que ela usa como queda) porque `hotel_planner_directives.ts` é
 *  exportado da raiz do pacote e não pode importar de `ui/`. */
export function hotelDistanceKm(
  offer: ChatUiHotelOffer,
  place: HotelPlannerPlace | undefined,
): number | undefined {
  if (offer.distanceKm !== undefined) return offer.distanceKm;
  if (!place || offer.latitude === undefined || offer.longitude === undefined) return undefined;
  return distanceKm(
    { lat: place.latitude, lng: place.longitude },
    { lat: offer.latitude, lng: offer.longitude },
  );
}
